import { useId } from 'react'
import type { ChangeEvent, ReactNode } from 'react'

/**
 * Champs de formulaire.
 *
 * Chaque champ relie son libellé, son message d'aide et son message d'erreur au contrôle
 * par `id` et `aria-describedby`. Ce n'est pas de la décoration : sans cela, un lecteur
 * d'écran annonce « champ de saisie » sans dire lequel, ni pourquoi il est en erreur.
 */

interface FieldProps {
  label: string
  htmlFor: string
  hint?: string
  error?: string
  required?: boolean
  children: ReactNode
}

export function Field({ label, htmlFor, hint, error, required, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-[0.85rem] font-medium">
        {label}
        {required === true && (
          <span className="ms-1 text-rouge" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {hint !== undefined && error === undefined && (
        <p id={`${htmlFor}-aide`} className="m-0 text-[0.8rem] text-texte-doux">
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={`${htmlFor}-erreur`} className="m-0 text-[0.8rem] text-rouge" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

interface TextFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  type?: 'text' | 'email' | 'password' | 'search' | 'number' | 'date' | 'time'
  placeholder?: string
  hint?: string
  error?: string
  required?: boolean
  disabled?: boolean
  /** Les nombres et les codes restent lus de gauche à droite, même en arabe. */
  ltr?: boolean
  /**
   * Bornes du calendrier natif, pour `type="date"`. Elles GUIDENT la saisie, elles ne la
   * garantissent pas : le navigateur laisse taper une date hors bornes au clavier, et rien
   * n'oblige un client à passer par cet écran. La règle vit dans le gestionnaire et dans
   * les déclencheurs de la base ; ceci évite d'y aller pour rien.
   */
  min?: string
  max?: string
}

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  error,
  required,
  disabled,
  ltr,
  min,
  max,
}: TextFieldProps) {
  const id = useId()

  return (
    <Field label={label} htmlFor={id} hint={hint} error={error} required={required}>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        min={min}
        max={max}
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? `${id}-erreur` : hint !== undefined ? `${id}-aide` : undefined}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        className={[
          'w-full',
          error !== undefined ? 'border-rouge' : '',
          ltr === true ? '[direction:ltr] [unicode-bidi:isolate] text-start' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      />
    </Field>
  )
}

export interface SelectOption {
  value: string
  label: string
}

interface SelectFieldProps {
  label: string
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  hint?: string
  error?: string
  required?: boolean
  disabled?: boolean
}

export function SelectField({ label, value, options, onChange, hint, error, required, disabled }: SelectFieldProps) {
  const id = useId()

  return (
    <Field label={label} htmlFor={id} hint={hint} error={error} required={required}>
      <select
        id={id}
        value={value}
        disabled={disabled}
        aria-invalid={error !== undefined}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
        className={`w-full ${error !== undefined ? 'border-rouge' : ''}`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  )
}

export function CheckboxField({
  label,
  checked,
  onChange,
  hint,
  disabled,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  hint?: string
  disabled?: boolean
}) {
  const id = useId()

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
          className="h-4 w-4 p-0"
        />
        <label htmlFor={id} className="text-[0.9rem]">
          {label}
        </label>
      </div>
      {hint !== undefined && <p className="m-0 ms-6 text-[0.8rem] text-texte-doux">{hint}</p>}
    </div>
  )
}

export function FormActions({ children }: { children: ReactNode }) {
  return <div className="mt-2 flex flex-wrap gap-2">{children}</div>
}
